/*
 * Search overlay.
 *
 * Opens the panel from [data-search-trigger] in the header, then swaps the
 * curated presets for live predictive results as the shopper types.
 *
 * Results come from Shopify's Predictive Search section-rendering endpoint
 * (/search/suggest?...&section_id=predictive-search →
 * sections/predictive-search.liquid). We lift [data-predictive-inner] out of the
 * response and drop it into [data-search-results].
 *
 * Open/close mirrors assets/header.js so the nav drawer and this behave the
 * same way: the shared page scroll lock (assets/scroll-lock.js), Escape to
 * close. It deliberately closes the nav drawer first if that is open, because
 * both sit over the page and two panels stacked read as a mistake.
 */

(function () {
  'use strict';

  /* Page scroll lock, shared with the other overlays (assets/scroll-lock.js). */
  function lockPage(on) {
    if (window.DDJScrollLock) window.DDJScrollLock.set('search', on);
    else document.body.style.overflow = on ? 'hidden' : '';
  }


  if (window.__ddjSearchInit) return;
  window.__ddjSearchInit = true;

  var DEBOUNCE_MS = 250;
  var MIN_TERM_LENGTH = 2;

  var drawer = null;
  var panel = null;
  var input = null;
  var presets = null;
  var results = null;
  var loading = null;
  var body = null;

  var isOpen = false;
  var lastFocused = null;
  var debounceTimer = null;
  var reqToken = 0;

  function cache() {
    drawer = document.querySelector('[data-search-drawer]');
    if (!drawer) return false;
    panel = drawer.querySelector('.search-drawer__panel');
    input = drawer.querySelector('[data-search-input]');
    presets = drawer.querySelector('[data-search-presets]');
    results = drawer.querySelector('[data-search-results]');
    loading = drawer.querySelector('[data-search-loading]');
    body = drawer.querySelector('[data-search-body]');
    return true;
  }

  /*
   * The announcement bar stays visible: Figma frames the panel below it and the
   * prototype keeps it on screen the whole time. Measure it rather than
   * hardcoding 32px, because the bar wraps to two lines on narrow viewports and
   * a merchant can empty it entirely (in which case it measures 0 and the panel
   * correctly goes full height).
   */
  function measureTop() {
    if (!drawer) return;
    var bar = document.querySelector('.announcement-bar-section');
    var top = bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
    drawer.style.setProperty('--search-drawer-top', top + 'px');
  }

  function root() {
    // Respects locale-prefixed roots (/en-gb/), which Shopify sets on
    // window.Shopify.routes.root. Falls back to the domain root.
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  /* ─── Open / close ─────────────────────────────────────────────────── */

  function closeNavDrawer() {
    // header.js owns the nav drawer and exposes no API, so reuse its toggle:
    // clicking it while open runs header.js's own close, which restores the
    // labels and tab state properly. Poking the DOM directly would desync it.
    var toggle = document.querySelector('[data-drawer-toggle]');
    if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
      toggle.click();
    }
  }

  function open() {
    if (!drawer || isOpen) return;
    closeNavDrawer();

    lastFocused = document.activeElement;
    isOpen = true;
    measureTop();
    drawer.hidden = false;

    // Force a reflow so the panel transitions in rather than appearing
    // already-open: `hidden` off and the class on in one frame is a no-op.
    void drawer.offsetWidth;
    drawer.classList.add('is-open');

    lockPage(true);
    setTriggerState(true);

    if (input) {
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }

  function close() {
    if (!drawer || !isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    lockPage(false);
    setTriggerState(false);

    var done = function () {
      if (!isOpen) drawer.hidden = true;
      panel.removeEventListener('transitionend', done);
    };
    if (panel) panel.addEventListener('transitionend', done);
    // Belt and braces: if the transition never fires (reduced motion, the tab
    // being backgrounded mid-close) the panel would stay visible but inert.
    setTimeout(done, 350);

    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function setTriggerState(expanded) {
    var triggers = document.querySelectorAll('[data-search-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  /* ─── Results ──────────────────────────────────────────────────────── */

  function showLoading() {
    if (loading) loading.hidden = false;
  }

  function hideLoading() {
    if (loading) loading.hidden = true;
  }

  function showPresets() {
    hideLoading();
    if (presets) presets.hidden = false;
    if (results) {
      results.hidden = true;
      results.innerHTML = '';
    }
  }

  function showResults(html) {
    hideLoading();
    if (presets) presets.hidden = true;
    if (results) {
      results.innerHTML = html;
      results.hidden = false;
    }
    if (body) body.scrollTop = 0;
  }

  function fetchResults(terms) {
    var token = ++reqToken;
    showLoading();

    var url = root() + 'search/suggest?q=' + encodeURIComponent(terms) +
      '&resources[type]=product,article,page' +
      '&resources[limit]=10' +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&section_id=predictive-search';

    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('suggest ' + response.status);
        return response.text();
      })
      .then(function (html) {
        // A newer keystroke has already superseded this request. Dropping it
        // here stops slow responses overwriting fresher results.
        if (token !== reqToken) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var inner = parsed.querySelector('[data-predictive-inner]');
        showResults(inner ? inner.innerHTML : '');
      })
      .catch(function () {
        if (token !== reqToken) return;
        // Fall back to the curated presets rather than an empty panel.
        showPresets();
      });
  }

  function onInput() {
    var terms = input.value.trim();
    clearTimeout(debounceTimer);

    if (terms.length < MIN_TERM_LENGTH) {
      reqToken++; // cancel any in-flight request
      showPresets();
      return;
    }

    debounceTimer = setTimeout(function () {
      fetchResults(terms);
    }, DEBOUNCE_MS);
  }

  function clearInput() {
    if (!input) return;
    input.value = '';
    reqToken++;
    showPresets();
    input.focus();
  }

  /* ─── Wiring ───────────────────────────────────────────────────────── */

  function init() {
    if (!cache()) return;

    // Delegated so header triggers work regardless of which markup rendered
    // them (desktop bar, mobile drawer) and survive a section re-render.
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-search-trigger]');
      if (trigger) {
        event.preventDefault();
        open();
        return;
      }
      if (event.target.closest('[data-search-close]')) {
        event.preventDefault();
        close();
        return;
      }
      if (event.target.closest('[data-search-clear]')) {
        event.preventDefault();
        clearInput();
      }
    });

    if (input) {
      input.addEventListener('input', onInput);
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen) close();
    });

    window.addEventListener('resize', function () {
      if (isOpen) measureTop();
    });

    // The theme editor re-renders the section on every setting change, which
    // replaces the nodes we cached and drops the open state.
    document.addEventListener('shopify:section:load', function (event) {
      if (event.target && event.target.querySelector('[data-search-drawer]')) {
        isOpen = false;
        cache();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
